import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import './Checkout.css';

const steps = ['Bag', 'Delivery', 'Payment', 'Confirm'];

function OrderSummary({ items, total }) {
  return (
    <div className="order-summary">
      <h3 className="summary-title">Your Order</h3>
      <div className="summary-items">
        {items.map(item => (
          <div className="summary-item" key={item.key}>
            <div className="summary-img">
              <img src={item.image} alt={item.name} />
              <span className="summary-qty">{item.quantity}</span>
            </div>
            <div className="summary-item-info">
              <p className="summary-item-name">{item.name}</p>
              <p className="summary-item-size">Size {item.size}</p>
            </div>
            <p className="summary-item-price">€{(item.price * item.quantity).toFixed(2)}</p>
          </div>
        ))}
      </div>
      <div className="summary-totals">
        <div className="summary-row">
          <span>Subtotal</span>
          <span>€{total.toFixed(2)}</span>
        </div>
        <div className="summary-row">
          <span>Shipping</span>
          <span>{total >= 250 ? <span className="free-tag">Complimentary</span> : '€12.00'}</span>
        </div>
        <div className="summary-row total-row">
          <span>Total</span>
          <span>€{(total >= 250 ? total : total + 12).toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}

export default function Checkout() {
  const { items, total, dispatch } = useCart();
  const navigate = useNavigate();
  const [step, setStep] = useState(items.length === 0 ? 0 : 1);
  const [placed, setPlaced] = useState(false);

  const [delivery, setDelivery] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    address: '', city: '', country: 'Ireland', postcode: ''
  });
  const [payment, setPayment] = useState({
    cardNumber: '', name: '', expiry: '', cvv: ''
  });
  const [errors, setErrors] = useState({});

  const handleDelivery = e => {
    setDelivery({ ...delivery, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };
  const handlePayment = e => {
    setPayment({ ...payment, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const validateDelivery = () => {
    const errs = {};
    ['firstName', 'lastName', 'email', 'address', 'city', 'postcode'].forEach(f => {
      if (!delivery[f].trim()) errs[f] = 'Required';
    });
    if (delivery.email && !/\S+@\S+\.\S+/.test(delivery.email)) errs.email = 'Invalid email';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validatePayment = () => {
    const errs = {};
    if (!payment.cardNumber.replace(/\s/g, '') || payment.cardNumber.replace(/\s/g, '').length < 16) errs.cardNumber = 'Invalid card number';
    if (!payment.name.trim()) errs.name = 'Required';
    if (!payment.expiry || !/^\d{2}\/\d{2}$/.test(payment.expiry)) errs.expiry = 'Use MM/YY';
    if (!payment.cvv || payment.cvv.length < 3) errs.cvv = 'Invalid CVV';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const formatCard = val => {
    return val.replace(/\D/g, '').substring(0, 16).replace(/(.{4})/g, '$1 ').trim();
  };
  const formatExpiry = val => {
    const clean = val.replace(/\D/g, '').substring(0, 4);
    return clean.length > 2 ? clean.substring(0,2) + '/' + clean.substring(2) : clean;
  };

  const placeOrder = () => {
    if (!validatePayment()) return;
    setPlaced(true);
    dispatch({ type: 'CLEAR_CART' });
  };

  if (placed) {
    return (
      <main className="checkout-page page-enter">
        <div className="order-success">
          <div className="success-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="9 12 11 14 15 10"/>
            </svg>
          </div>
          <p className="success-eyebrow">Order Confirmed</p>
          <h2 className="success-title">Thank you, {delivery.firstName}.</h2>
          <p className="success-sub">
            Your pieces are being carefully prepared for dispatch. You'll receive a confirmation at <strong>{delivery.email}</strong>.
          </p>
          <p className="success-order">Order #ALN-{Math.floor(10000 + Math.random() * 90000)}</p>
          <Link to="/" className="success-btn">Return to Alainn</Link>
        </div>
      </main>
    );
  }

  if (items.length === 0) {
    return (
      <main className="checkout-page page-enter">
        <div className="empty-checkout">
          <h2 className="empty-title">Your bag is empty</h2>
          <p className="empty-sub">Add some pieces before checking out.</p>
          <Link to="/shop" className="success-btn">Browse Collection</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="checkout-page page-enter">
      <div className="checkout-inner">
        {/* Left col */}
        <div className="checkout-form-col">
          <Link to="/" className="checkout-logo">Alainn</Link>

          {/* Steps */}
          <div className="checkout-steps">
            {steps.slice(1).map((s, i) => (
              <React.Fragment key={s}>
                <div className={`step-dot ${step === i + 1 ? 'active' : step > i + 1 ? 'done' : ''}`}>
                  {step > i + 1
                    ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                    : <span>{i + 1}</span>
                  }
                </div>
                <span className={`step-label ${step === i + 1 ? 'active' : ''}`}>{s}</span>
                {i < steps.length - 2 && <div className={`step-line ${step > i + 1 ? 'done' : ''}`} />}
              </React.Fragment>
            ))}
          </div>

          {/* Step 1 — Delivery */}
          {step === 1 && (
            <div className="form-section">
              <h2 className="form-title">Delivery Details</h2>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input name="firstName" value={delivery.firstName} onChange={handleDelivery} placeholder="Siobhán" className={errors.firstName ? 'error' : ''} />
                  {errors.firstName && <span className="field-error">{errors.firstName}</span>}
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input name="lastName" value={delivery.lastName} onChange={handleDelivery} placeholder="Murphy" className={errors.lastName ? 'error' : ''} />
                  {errors.lastName && <span className="field-error">{errors.lastName}</span>}
                </div>
              </div>
              <div className="form-group">
                <label>Email Address</label>
                <input name="email" type="email" value={delivery.email} onChange={handleDelivery} placeholder="you@example.com" className={errors.email ? 'error' : ''} />
                {errors.email && <span className="field-error">{errors.email}</span>}
              </div>
              <div className="form-group">
                <label>Phone (optional)</label>
                <input name="phone" value={delivery.phone} onChange={handleDelivery} placeholder="+353 87 000 0000" />
              </div>
              <div className="form-group">
                <label>Address</label>
                <input name="address" value={delivery.address} onChange={handleDelivery} placeholder="12 Grafton Street" className={errors.address ? 'error' : ''} />
                {errors.address && <span className="field-error">{errors.address}</span>}
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input name="city" value={delivery.city} onChange={handleDelivery} placeholder="Dublin" className={errors.city ? 'error' : ''} />
                  {errors.city && <span className="field-error">{errors.city}</span>}
                </div>
                <div className="form-group">
                  <label>Postcode</label>
                  <input name="postcode" value={delivery.postcode} onChange={handleDelivery} placeholder="D02 XY00" className={errors.postcode ? 'error' : ''} />
                  {errors.postcode && <span className="field-error">{errors.postcode}</span>}
                </div>
              </div>
              <div className="form-group">
                <label>Country</label>
                <select name="country" value={delivery.country} onChange={handleDelivery}>
                  <option>Ireland</option>
                  <option>United Kingdom</option>
                  <option>France</option>
                  <option>Germany</option>
                  <option>Italy</option>
                  <option>Spain</option>
                  <option>Netherlands</option>
                  <option>Other</option>
                </select>
              </div>
              <button className="form-next-btn" onClick={() => validateDelivery() && setStep(2)}>
                Continue to Payment →
              </button>
            </div>
          )}

          {/* Step 2 — Payment */}
          {step === 2 && (
            <div className="form-section">
              <h2 className="form-title">Payment</h2>
              <div className="payment-security">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0110 0v4"/>
                </svg>
                <span>Secure SSL encrypted payment</span>
              </div>
              <div className="card-brands">
                {['VISA', 'MC', 'AMEX'].map(b => (
                  <span key={b} className="card-brand">{b}</span>
                ))}
              </div>
              <div className="form-group">
                <label>Card Number</label>
                <input
                  name="cardNumber"
                  value={payment.cardNumber}
                  onChange={e => setPayment({ ...payment, cardNumber: formatCard(e.target.value) })}
                  placeholder="0000 0000 0000 0000"
                  maxLength={19}
                  className={errors.cardNumber ? 'error' : ''}
                />
                {errors.cardNumber && <span className="field-error">{errors.cardNumber}</span>}
              </div>
              <div className="form-group">
                <label>Name on Card</label>
                <input name="name" value={payment.name} onChange={handlePayment} placeholder="SIOBHÁN MURPHY" className={errors.name ? 'error' : ''} />
                {errors.name && <span className="field-error">{errors.name}</span>}
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Expiry Date</label>
                  <input
                    name="expiry"
                    value={payment.expiry}
                    onChange={e => setPayment({ ...payment, expiry: formatExpiry(e.target.value) })}
                    placeholder="MM/YY"
                    maxLength={5}
                    className={errors.expiry ? 'error' : ''}
                  />
                  {errors.expiry && <span className="field-error">{errors.expiry}</span>}
                </div>
                <div className="form-group">
                  <label>CVV</label>
                  <input
                    name="cvv"
                    value={payment.cvv}
                    onChange={e => setPayment({ ...payment, cvv: e.target.value.replace(/\D/g, '').substring(0, 4) })}
                    placeholder="•••"
                    maxLength={4}
                    className={errors.cvv ? 'error' : ''}
                  />
                  {errors.cvv && <span className="field-error">{errors.cvv}</span>}
                </div>
              </div>
              <div className="form-actions">
                <button className="form-back-btn" onClick={() => setStep(1)}>← Back</button>
                <button className="form-next-btn" onClick={placeOrder}>
                  Place Order · €{(total >= 250 ? total : total + 12).toFixed(2)}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right col — Order summary */}
        <div className="checkout-summary-col">
          <OrderSummary items={items} total={total} />
        </div>
      </div>
    </main>
  );
}
